import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiProvider } from '@restaurant/api-client';

import { App } from './App';
import { IdleLock } from './lock';
import { startTillApp } from './native';
import { PracticeBand } from './practice';
import { OfflineGate } from './offline/gate';
import { Shell } from './shell';

import { startErrorReporting, startTheme } from '@restaurant/ui';
import '@restaurant/ui/tokens.css';

// Which room this is. Type scale, hit targets and density all resolve from
// it — see the note at the top of tokens.css.
document.documentElement.dataset.room = 'hand';

// First, so an error in the first render is reported too. A no-op unless this
// deployment has a DSN (packages/ui/src/error-reporting.ts).
startErrorReporting('pos');

// Before the first render, so a dark device never flashes the light ground.
startTheme();

// Inside the till app (apps/till-app), printing goes through Android. A no-op
// in a browser (native.ts).
startTillApp();

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    {/* Same origin in dev via the Vite proxy, and behind a reverse proxy in
        production — so the client never needs a configured API host. */}
    <ApiProvider baseUrl="" storageKey="restaurant.pos.token">
      {/* Above everything, offline and locked included: a practising till
          must never be mistaken for one that counts. */}
      <PracticeBand />
      {/* Above the shell: with no connection the shell cannot check the
          account and the till cannot load its site, so a signed-in tablet
          gets the offline till in their place (`offline/`). */}
      <OfflineGate>
        <Shell appName="Point of Sale" tagline="Sign in to open the floor.">
          <App />
          {/* Beside the app rather than inside it, so a PIN swap that
              remounts the app does not take the lock's own state with it. */}
          <IdleLock />
        </Shell>
      </OfflineGate>
    </ApiProvider>
  </StrictMode>,
);

// The till loads with no connection: a service worker keeps the app itself
// (public/sw.js). Production only — in development it would serve yesterday's
// build over the dev server.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
